import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { SetGoAccountsPayload } from 'types';
import { getApiErrorMessage } from 'utils';

import { useAuthSession } from 'modules/auth-session';

import {
  useDisconnectAccountMutation,
  useGetGoAccountsMutation,
  useSaveAccountMutation,
  useSetGoAccountsMutation,
} from '../api/necs-account.api';
import {
  NecsAccountAC,
  setGoAccounts,
  setNecsAccount,
  setNecsAccountConnectionResult,
} from '../store/necs-account.slice';
import { SaveNecsAccountPayload } from '../types/necs-account.types';

interface UseNecsAccount {
  saveNecsAccountHandler: (data: SaveNecsAccountPayload) => Promise<void>;
  disconnectNecsAccountHandler: () => Promise<void>;
  getGoAccountsHandler: () => Promise<void>;
  setGoAccountsHandler: (data: SetGoAccountsPayload) => Promise<boolean>;
}

export const useNecsAccount = (): UseNecsAccount => {
  const dispatch = useAppDispatch();
  const { checkSessionHandler } = useAuthSession();

  const [saveNecsAccountQuery] = useSaveAccountMutation();
  const [disconnectNecsAccountQuery] = useDisconnectAccountMutation();
  const [getGoAccountsQuery] = useGetGoAccountsMutation();
  const [setGoAccountsQuery] = useSetGoAccountsMutation();

  const saveNecsAccountHandler = async (payload: SaveNecsAccountPayload) => {
    dispatch(NecsAccountAC.toggleLoading(true));
    const { data: response, error } = await saveNecsAccountQuery(payload);
    const isError = error || !response;

    if (isError) {
      dispatch(NecsAccountAC.toggleLoading(false));
      if (error && 'data' in error) {
        const data = error.data as any;
        if (data?.necsError) {
          dispatch(
            setNecsAccountConnectionResult({
              state: 'necs-error',
              message: data.necsError,
            }),
          );
          return;
        }
      }
      toast.error(getApiErrorMessage(error) || 'Failed to connect NECS account!');
    }

    if (response) {
      setTimeout(() => {
        dispatch(setNecsAccount(response.data));
        dispatch(
          setNecsAccountConnectionResult({
            state: 'success',
            message:
              'Your NECS account was successfully connected, the certificate bundles will soon appear in our system. You can now also select which GO Accounts will be used for selling and buying certificates.',
          }),
        );
        dispatch(NecsAccountAC.toggleLoading(false));
      }, 5000);
    }
  };

  const disconnectNecsAccountHandler = async () => {
    dispatch(NecsAccountAC.toggleLoading(true));
    const { error } = await disconnectNecsAccountQuery({});
    dispatch(NecsAccountAC.toggleLoading(false));

    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to disconnect NECS account!');
      return;
    }

    toast.success('NECS account successfully disconnected!');
    dispatch(setGoAccounts([]));
    dispatch(setNecsAccount(null));
  };

  const getGoAccountsHandler = async () => {
    dispatch(NecsAccountAC.toggleLoading(true));
    const { data: response, error } = await getGoAccountsQuery();
    dispatch(NecsAccountAC.toggleLoading(false));
    const isError = error || !response?.data;

    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get GO accounts!');
      return;
    }
    dispatch(setGoAccounts(response.data));
  };

  const setGoAccountsHandler = async (payload: SetGoAccountsPayload) => {
    dispatch(NecsAccountAC.toggleLoading(true));
    const { error } = await setGoAccountsQuery(payload);

    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to set GO accounts!');
      dispatch(NecsAccountAC.toggleLoading(false));
      return false;
    }

    await getGoAccountsHandler();
    await checkSessionHandler();
    dispatch(NecsAccountAC.toggleLoading(false));
    return true;
  };

  return {
    disconnectNecsAccountHandler,
    getGoAccountsHandler,
    saveNecsAccountHandler,
    setGoAccountsHandler,
  };
};
